import { ApplicationConfig, provideZoneChangeDetection, provideAppInitializer, inject, provideBrowserGlobalErrorListeners, ErrorHandler } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MsalGuard, MsalService, MSAL_INSTANCE, MsalInterceptor, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService } from '@azure/msal-angular';
import { providePrimeNG } from 'primeng/config';
import { provideNgIdle } from '@ng-idle/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { primengTheme } from '@core/theme/primeng-theme';
import { PRIMENG_ES } from '@shared/config/primeng-es.config';

import { routes } from './app.routes';
import { coreConfig } from '@core/core.config';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withInterceptors } from '@angular/common/http';
import { msalGuardConfigFactory, msalInstanceFactory, msalInterceptorConfigFactory } from './msal-configuration';
import { AuthService } from '@core/services/auth.service';
import { MsalConfigService } from '@core/services/msal-configuration-service';
import { HostConfigService } from '@core/services/host-config.service';
import { GestorSesionUsuarioService } from '@core/services/gestor-sesion-usuario.service';
import { CargaGlobalService } from '@shared/servicios/carga-global.service';
import { ModalService } from '@core/services/modal.service';
import { InactividadService } from '@core/services/inactividad.service';
import { PrevisualizacionService } from '@shared/servicios/previsualizacion.service';
import { BadgeMenuService } from '@core/services/badge-menu.service';
import { CONTEO_SOLICITUDES_PORT } from '@core/interfaces/conteo-solicitudes-port';
import { SolicitudesAprobacionService } from './feature/solicitudes-aprobacion/services/solicitudes-aprobacion.service';
import { empresaInterceptor } from './core/interceptors/empresa.interceptor';
import { erroresHttpInterceptor } from '@core/interceptors/errores-http/errores-http.interceptor';
import { autenticacionInterceptor } from '@core/interceptors/autenticacion/autenticacion.interceptor';
import { cargaGlobalInterceptor } from '@core/interceptors/carga-global/carga-global.interceptor';
import { TelemetriaService } from '@core/services/telemetria.service';
import { ManejadorErroresService } from '@core/services/manejador-errores/manejador-errores.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(
      withInterceptors([cargaGlobalInterceptor, empresaInterceptor, erroresHttpInterceptor, autenticacionInterceptor]),
      withInterceptorsFromDi()
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: primengTheme,
        options: {
          darkModeSelector: false
        }
      },
      translation: PRIMENG_ES
    }),
    { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactory },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    AuthService,
    MsalConfigService,
    HostConfigService,
    GestorSesionUsuarioService,
    MessageService,
    ConfirmationService,
    DialogService,
    CargaGlobalService,
    ModalService,
    InactividadService,
    provideNgIdle(),
    PrevisualizacionService,
    BadgeMenuService,
    SolicitudesAprobacionService,
    { provide: CONTEO_SOLICITUDES_PORT, useExisting: SolicitudesAprobacionService },
    { provide: ErrorHandler, useClass: ManejadorErroresService },
    provideBrowserGlobalErrorListeners(),
    provideAppInitializer(() => {
      inject(TelemetriaService).inicializar();
    }),
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      return authService.inicializar();
    }),
    ...coreConfig.providers,
  ],
};
